import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, CheckCircle, XCircle, Edit2, Send, RefreshCw, Clock, MessageSquare, AlertTriangle, Link2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewResponses = () => {
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [templates, setTemplates] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [googleAuthStatus, setGoogleAuthStatus] = useState(null);

  useEffect(() => {
    fetchResponses();
    fetchStats();
    fetchTemplates();
    fetchGoogleAuthStatus();
    
    // Check URL params for auth callback
    const params = new URLSearchParams(window.location.search);
    const authCode = params.get('code');
    const authState = params.get('state');
    
    if (authCode) {
      // Handle OAuth callback - exchange code for tokens
      handleOAuthCallback(authCode, authState);
    } else if (params.get('auth') === 'success') {
      alert('✅ Google Business Profile connected successfully!');
      window.history.replaceState({}, '', '/admin/review-responses');
      fetchGoogleAuthStatus();
    } else if (params.get('error')) {
      alert('❌ Google authentication failed: ' + params.get('error'));
      window.history.replaceState({}, '', '/admin/review-responses');
    }
  }, []);

  useEffect(() => {
    fetchResponses();
  }, [filter]);

  const handleOAuthCallback = async (code, state) => {
    try {
      setLoading(true);
      // Send code to backend to exchange for tokens
      const res = await fetch(`${API_URL}/api/google-business/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`);
      if (res.ok) {
        alert('✅ Google Business Profile connected successfully!');
        fetchGoogleAuthStatus();
      } else {
        const data = await res.json();
        alert('❌ Failed to connect: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
      alert('❌ Error connecting to Google Business Profile');
    } finally {
      // Clean up URL
      window.history.replaceState({}, '', '/admin/review-responses');
      setLoading(false);
    }
  };

  const fetchGoogleAuthStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/google-business/auth/status`);
      if (res.ok) {
        const data = await res.json();
        setGoogleAuthStatus(data);
      }
    } catch (err) {
      console.error('Error fetching Google auth status:', err);
    }
  };

  const connectGoogleBusiness = () => {
    // Navigate directly to the OAuth start endpoint
    // This allows the browser to handle the redirect to Google naturally
    window.location.href = `${API_URL}/api/google-business/auth/start`;
  };

  const disconnectGoogleBusiness = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Business Profile? You will need to reconnect to sync reviews.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/google-business/auth/disconnect`, {
        method: 'POST'
      });
      if (res.ok) {
        alert('✅ Google Business Profile disconnected successfully');
        setGoogleAuthStatus({ authenticated: false });
      } else {
        const data = await res.json();
        alert('❌ Failed to disconnect: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      alert('Error disconnecting. Please try again.');
    }
  };

  const syncFromGoogle = async () => {
    if (!googleAuthStatus?.authenticated) {
      alert('Please connect to Google Business Profile first');
      return;
    }
    
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/review-responses/sync-from-google`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.new_reviews > 0) {
          alert(`✅ Synced ${data.new_reviews} new reviews from Google!\n\n5-star reviews are auto-approved.\nLower-rated reviews need your approval.`);
        } else {
          alert('✓ All reviews are already synced. No new reviews found.');
        }
        // Refresh the data
        fetchResponses();
        fetchStats();
      } else {
        const data = await res.json();
        alert('❌ Sync failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error syncing reviews:', err);
      alert('❌ Error syncing reviews from Google');
    } finally {
      setSyncing(false);
    }
  };

  const purgeForeignReviews = async () => {
    if (!window.confirm('Remove reviews that don\'t belong to Fort Worth Dental (e.g. pulled from another business)? You can re-sync afterwards to pull the correct ones.')) return;
    try {
      const res = await fetch(`${API_URL}/api/review-responses/purge-foreign`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}`);
        fetchResponses();
        fetchStats();
      } else {
        alert('❌ Cleanup failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('❌ Error cleaning up reviews');
    }
  };


  const fetchResponses = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/review-responses/all`
        : `${API_URL}/api/review-responses/all?status=${filter}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setResponses(data.responses || []);
      }
    } catch (err) {
      console.error('Error fetching responses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/review-responses/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/review-responses/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || {});
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleApproval = async (responseId, action, editedResponse = null) => {
    try {
      const res = await fetch(`${API_URL}/api/review-responses/approve/${responseId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          response_id: responseId, 
          action, 
          edited_response: editedResponse 
        })
      });
      
      if (res.ok) {
        setEditingId(null);
        setEditText('');
        fetchResponses();
        fetchStats();
      }
    } catch (err) {
      console.error('Error processing approval:', err);
    }
  };

  const handlePost = async (responseId) => {
    try {
      const res = await fetch(`${API_URL}/api/review-responses/post/${responseId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.requires_setup) {
          alert('Google Business Profile API setup required. See console for instructions.');
          console.log('Setup Instructions:', data.setup_instructions);
        }
        fetchResponses();
        fetchStats();
      }
    } catch (err) {
      console.error('Error posting response:', err);
    }
  };

  const renderStars = (rating) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
        />
      ))}
    </div>
  );

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      posted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Send }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Review Responses | Admin Dashboard</title>
      </Helmet>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-teal-600" />
            Review Response Management
          </h1>
          <p className="text-gray-600 mt-1">
            AI-generated responses with approval workflow for non-5-star reviews
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-sm text-gray-600">Approved</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.posted}</p>
              <p className="text-sm text-gray-600">Posted</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
          </div>
        )}

        {/* Google Business Connection Status */}
        <div className={`rounded-lg p-4 mb-6 ${googleAuthStatus?.authenticated ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link2 className={`w-5 h-5 ${googleAuthStatus?.authenticated ? 'text-green-600' : 'text-orange-600'}`} />
              <div>
                <h3 className={`font-semibold ${googleAuthStatus?.authenticated ? 'text-green-800' : 'text-orange-800'}`}>
                  {googleAuthStatus?.authenticated
                    ? '✅ Google Business Profile Connected'
                    : (googleAuthStatus?.needs_reconnect
                        ? '⚠️ Google Connection Expired — Reconnect Needed'
                        : '⚠️ Google Business Profile Not Connected')}
                </h3>
                <p className={`text-sm ${googleAuthStatus?.authenticated ? 'text-green-700' : 'text-orange-700'}`}>
                  {googleAuthStatus?.authenticated
                    ? 'You can sync reviews and post responses directly to Google'
                    : (googleAuthStatus?.needs_reconnect
                        ? 'Your Google session expired (this happens periodically). Click Disconnect, then Connect Google Business to restore syncing and posting.'
                        : 'Connect your Google Business account to sync reviews')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {googleAuthStatus?.authenticated ? (
                <>
                  <button
                    onClick={syncFromGoogle}
                    disabled={syncing}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition disabled:opacity-50"
                    data-testid="sync-reviews-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync Reviews from Google'}
                  </button>
                  <button
                    onClick={disconnectGoogleBusiness}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    data-testid="disconnect-google-btn"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectGoogleBusiness}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  data-testid="connect-google-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Connect Google Business
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-teal-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-teal-800">How It Works</h3>
              <ul className="text-sm text-teal-700 mt-1 space-y-1">
                <li>• <strong>5-star reviews:</strong> AI generates response and auto-approves immediately</li>
                <li>• <strong>1-4 star reviews:</strong> AI generates response, queued for your approval</li>
                <li>• You can approve, edit, or reject any response before posting</li>
                <li>• Email notifications are sent for pending approvals</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected', 'posted', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === f 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && stats?.pending > 0 && (
                <span className="ml-2 bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs">
                  {stats.pending}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={() => { fetchResponses(); fetchStats(); }}
            className="ml-auto px-4 py-2 rounded-lg bg-white border hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={purgeForeignReviews}
            data-testid="purge-foreign-btn"
            className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-2"
            title="Remove reviews that don't belong to Fort Worth Dental"
          >
            <AlertTriangle className="w-4 h-4" />
            Clean Up Foreign Reviews
          </button>
        </div>

        {/* Responses List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading responses...</p>
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No {filter === 'all' ? '' : filter} responses found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {responses.map((response) => (
              <div key={response.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Review Header */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                        {response.reviewer_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{response.reviewer_name}</h3>
                        <div className="flex items-center gap-2">
                          {renderStars(response.rating)}
                          <span className="text-sm text-gray-500">{response.review_date}</span>
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(response.status)}
                  </div>
                </div>

                {/* Review Content */}
                <div className="p-4">
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Customer Review:</p>
                    <p className="text-gray-700 italic">&quot;{response.review_text}&quot;</p>
                  </div>

                  {/* Generated Response */}
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-sm text-teal-700 mb-2 font-medium">AI-Generated Response:</p>
                    {editingId === response.id ? (
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full p-3 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        rows={4}
                      />
                    ) : (
                      <p className="text-gray-800">{response.generated_response}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {response.status === 'pending' && (
                      <>
                        {editingId === response.id ? (
                          <>
                            <button
                              onClick={() => handleApproval(response.id, 'approve', editText)}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Save & Approve
                            </button>
                            <button
                              onClick={() => { setEditingId(null); setEditText(''); }}
                              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleApproval(response.id, 'approve')}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => { setEditingId(response.id); setEditText(response.generated_response); }}
                              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleApproval(response.id, 'reject')}
                              className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </>
                        )}
                      </>
                    )}
                    
                    {response.status === 'approved' && (
                      <button
                        onClick={() => handlePost(response.id)}
                        className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                      >
                        <Send className="w-4 h-4" />
                        Post to Google
                      </button>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div className="mt-4 text-xs text-gray-500 space-y-1">
                    <p>Created: {new Date(response.created_at).toLocaleString()}</p>
                    {response.approved_at && (
                      <p>Approved: {new Date(response.approved_at).toLocaleString()} by {response.approved_by}</p>
                    )}
                    {response.posted_at && (
                      <p>Posted: {new Date(response.posted_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Templates Section */}
        <div className="mt-8">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
          >
            {showTemplates ? '▼' : '▶'} Response Templates by Rating
          </button>
          
          {showTemplates && (
            <div className="mt-4 grid gap-4">
              {Object.entries(templates).map(([rating, data]) => (
                <div key={rating} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {renderStars(parseInt(rating))}
                    <span className="font-medium text-gray-700">
                      {rating}-Star Response ({data.style})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {data.templates?.map((template, idx) => (
                      <p key={idx} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {template}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewResponses;
